import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  UserCircle,
  ScrollText,
  CalendarCheck2,
  ArrowRight,
  LogOut,
  ExternalLink,
} from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import BrandLogo from "@/components/BrandLogo";

const CUR = "\u20b9";
const fmt = (n) => (n === null || n === undefined ? "—" : `${CUR}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
const statusColor = {
  submitted: "bg-muted text-secondary",
  prepared: "bg-accent/20 text-secondary",
  approved: "bg-primary/10 text-primary",
  declined: "bg-destructive/10 text-destructive",
  expired: "bg-muted text-muted-foreground",
  confirmed: "bg-primary/10 text-primary",
  in_progress: "bg-accent/20 text-secondary",
  completed: "bg-primary/10 text-primary",
  cancelled: "bg-destructive/10 text-destructive",
  payment_pending: "bg-accent/20 text-secondary",
};

export default function CustomerDashboard() {
  const { customerInfo, logoutCustomer } = useApp();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [q, b] = await Promise.all([
          api.get("/quotes/mine"),
          api.get("/bookings/mine"),
        ]);
        setQuotes(q.data || []);
        setBookings(b.data || []);
      } catch (_) { /* leave lists empty */ }
      finally { setLoading(false); }
    })();
  }, []);

  const doLogout = () => { logoutCustomer(); navigate("/"); };

  return (
    <div className="min-h-screen bg-muted/40">
      <header className="border-b border-border/70 bg-card">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" data-testid="account-dashboard-brand" className="flex items-center gap-3">
            <BrandLogo />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="account-dashboard-home" className="text-sm font-medium text-secondary hover:text-primary">Back to Home</Link>
            <button onClick={doLogout} data-testid="account-dashboard-logout" className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10">
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-10">
        <div className="max-w-2xl">
          <span className="kbd-eyebrow">Account</span>
          <h1 className="mt-3 font-serif-display text-3xl font-semibold text-secondary sm:text-4xl" data-testid="dash-welcome">
            Welcome{customerInfo?.name ? `, ${customerInfo.name}` : ""}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Track your quotes and bookings with Tezz Khana.
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <Tile icon={UserCircle} title="Profile" desc="View and edit your contact details" to="/account/profile" testid="dash-profile" />
          <Tile icon={ScrollText} title="Quotes" desc={`${quotes.length} on record`} to="#quotes" testid="dash-quotes" />
          <Tile icon={CalendarCheck2} title="Bookings" desc={`${bookings.length} on record`} to="#bookings" testid="dash-bookings" />
        </div>

        <section id="quotes" className="surface-card mt-8" data-testid="dash-quotes-section">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Your quotes</div>
            <span className="text-xs text-muted-foreground">{quotes.length} total</span>
          </div>
          {loading && <div className="mt-3 text-xs text-muted-foreground">Loading…</div>}
          {!loading && quotes.length === 0 && (
            <div className="mt-3 rounded-xl border border-dashed border-border/70 bg-background p-4 text-xs text-muted-foreground" data-testid="dash-quotes-empty">
              No quotes yet. Our team submits quotes for you after your enquiry.
            </div>
          )}
          {quotes.length > 0 && (
            <ul className="mt-3 divide-y divide-border/60">
              {quotes.map((q) => (
                <li key={q.id} data-testid={`dash-quote-${q.id}`} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-secondary">
                      {q.event_type || "Event"} · {q.event_date || "date TBC"} · {q.guest_count || "—"} guests
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                      {q.package_snapshot?.name || "—"} · {(q.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${statusColor[q.status] || "bg-muted text-secondary"}`}>{q.status}</span>
                    <span className="hidden text-xs text-secondary sm:inline">{fmt(q.final_amount)}</span>
                    <Link to={`/account/quotes/${q.id}`} data-testid={`dash-quote-open-${q.id}`} className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-semibold text-secondary hover:bg-muted">
                      Open <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section id="bookings" className="surface-card mt-6" data-testid="dash-bookings-section">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Your bookings</div>
            <span className="text-xs text-muted-foreground">{bookings.length} total</span>
          </div>
          {loading && <div className="mt-3 text-xs text-muted-foreground">Loading…</div>}
          {!loading && bookings.length === 0 && (
            <div className="mt-3 rounded-xl border border-dashed border-border/70 bg-background p-4 text-xs text-muted-foreground" data-testid="dash-bookings-empty">
              No bookings yet. Approving a prepared quote will confirm your event booking.
            </div>
          )}
          {bookings.length > 0 && (
            <ul className="mt-3 divide-y divide-border/60">
              {bookings.map((b) => (
                <li key={b.id} data-testid={`dash-booking-${b.reference}`} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-mono font-semibold text-secondary">{b.reference}</div>
                    <div className="truncate text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                      {b.package_snapshot?.name || "—"} · {b.event_date || "date TBC"} · {b.guest_count || "—"} guests · {fmt(b.final_amount)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${statusColor[b.status] || "bg-muted text-secondary"}`}>{b.status}</span>
                    <Link to={`/account/bookings/${b.id}`} data-testid={`dash-booking-open-${b.reference}`} className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-semibold text-secondary hover:bg-muted">
                      Open <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="surface-card mt-6">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Account</div>
          <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
            <div><dt className="text-muted-foreground">Name</dt><dd className="font-medium text-secondary" data-testid="dash-profile-name">{customerInfo?.name || "—"}</dd></div>
            <div><dt className="text-muted-foreground">Email</dt><dd className="font-medium text-secondary" data-testid="dash-profile-email">{customerInfo?.email || "—"}</dd></div>
            <div><dt className="text-muted-foreground">Mobile</dt><dd className="font-medium text-secondary" data-testid="dash-profile-mobile">{customerInfo?.mobile || "—"}</dd></div>
            <div><dt className="text-muted-foreground">WhatsApp</dt><dd className="font-medium text-secondary" data-testid="dash-profile-whatsapp">{customerInfo?.whatsapp || "—"}</dd></div>
          </dl>
        </section>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, title, desc, to, testid }) {
  const body = (
    <div className="surface-card flex items-start justify-between gap-3 transition-transform hover:-translate-y-0.5 hover:shadow-md">
      <div>
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="mt-4 font-serif-display text-lg font-semibold text-secondary">{title}</div>
        <div className="mt-1 text-xs text-muted-foreground">{desc}</div>
      </div>
      <ArrowRight className="mt-3 h-4 w-4 text-primary" aria-hidden />
    </div>
  );
  if (to.startsWith("#")) {
    return <a href={to} data-testid={testid}>{body}</a>;
  }
  return <Link to={to} data-testid={testid}>{body}</Link>;
}
